// ===========================================
// Public Legal Page (Terms of Service / Privacy Policy)
// ===========================================
// Styled to match the Meet & Greet booking page

import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import { usePublicLegal } from '../../hooks/usePublicLegal';
import PublicFooter from '../../components/public/PublicFooter';


export default function LegalPage() {
  const { schoolSlug, type } = useParams<{ schoolSlug: string; type: string }>();
  const navigate = useNavigate();
  const effectiveSlug = schoolSlug || 'music-n-me';
  const isTerms = type === 'terms';
  const isPrivacy = type === 'privacy';
  const isCookies = type === 'cookies';

  const { data, isLoading, error } = usePublicLegal(effectiveSlug);

  let title = 'Terms of Service';
  let content: string | null | undefined;

  if (isTerms) {
    title = 'Terms of Service';
    content = data?.termsOfService;
  } else if (isPrivacy) {
    title = 'Privacy Policy';
    content = data?.privacyPolicy;
  } else if (isCookies) {
    title = 'Cookie Policy';
    content = data?.cookiePolicy;
  }

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', py: 8 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            Unable to load {title.toLowerCase()}. Please try again.
          </Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Mobile Header */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{ maxWidth: 120, height: 'auto', mb: 2 }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Desktop Header */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '2rem', md: '2.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
        {data?.schoolName && (
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', color: 'text.secondary', mb: 3 }}
          >
            {data.schoolName}
          </Typography>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, mb: 3 }}>
          {content ? (
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
            >
              {content}
            </Typography>
          ) : (
            <Alert severity="info">
              No {title.toLowerCase()} has been published yet.
            </Alert>
          )}
        </Paper>

        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            &larr; Go Back
          </Button>
        </Box>
      </Container>

      <PublicFooter schoolName={data?.schoolName} schoolSlug={effectiveSlug} />
    </Box>
  );
}
